import { Link } from "react-router-dom";
import { Plus, Tag, Star } from "lucide-react";
import { useCart } from "../lib/cart";
import { discountSticker } from "../lib/promos";
import { withTax, fmtRound } from "../lib/tax";
import { brandSlug } from "../lib/brand";
import { prefetchProduct, prefetchBrand } from "../lib/api";

function StrainPill({ strain }) {
    if (!strain || strain === "NOT_APPLICABLE") return null;
    const map = {
        INDICA: { bg: "bg-purple-100", text: "text-purple-700", label: "Indica" },
        SATIVA: { bg: "bg-orange-100", text: "text-orange-700", label: "Sativa" },
        HYBRID: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Hybrid" },
        INDICA_HYBRID: { bg: "bg-purple-100", text: "text-purple-700", label: "Indica-Hybrid" },
        SATIVA_HYBRID: { bg: "bg-orange-100", text: "text-orange-700", label: "Sativa-Hybrid" },
        HIGH_CBD: { bg: "bg-sky-100", text: "text-sky-700", label: "High CBD" },
        THC: { bg: "bg-emerald-100", text: "text-emerald-700", label: "THC" },
    };
    const m = map[strain] || { bg: "bg-zen-ink/10", text: "text-zen-ink-soft", label: strain };
    return (
        <span className={`px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${m.bg} ${m.text}`}>
            {m.label}
        </span>
    );
}

export default function ProductCard({ product, storeSlug, specialsById = {} }) {
    const { addToCart, loading } = useCart();
    const img = product.images?.[0]?.url;
    const inStock = product.variants?.some((v) => v.quantity > 0);
    const firstVariant = product.variants?.[0];
    const isOnSale = firstVariant?.specialPriceRec && firstVariant.specialPriceRec < firstVariant.priceRec;
    const price = firstVariant?.specialPriceRec || firstVariant?.priceRec;

    // Real product↔special association via Dutchie's specialConditionIds /
    // specialRewardIds on each variant. We pick the first applicable one and
    // use Dutchie's raw `name` directly (no client-side normalization).
    const applicableSpecial = (() => {
        if (!firstVariant) return null;
        const ids = [
            ...(firstVariant.specialConditionIds || []),
            ...(firstVariant.specialRewardIds || []),
        ];
        for (const id of ids) {
            if (specialsById[id]) return specialsById[id];
        }
        return null;
    })();

    // Sticker shown on top of the image so customers can see "% OFF" vs "$ OFF"
    // at a glance. Derived from the variant's sale price OR a PCT_OFF special.
    const sticker = discountSticker(firstVariant, applicableSpecial);

    const handleAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!inStock || !firstVariant) return;
        addToCart({
            slug: storeSlug,
            productId: product.id,
            option: firstVariant.option || "",
            quantity: 1,
        }).catch(() => {});
    };

    return (
        <article
            data-testid={`product-card-${product.slug}`}
            className="group relative bg-zen-bg border border-zen-ink/10 hover:border-zen-olive/60 transition-all duration-500 overflow-hidden flex flex-col"
        >
            <Link
                to={`/menu/${storeSlug}/${product.slug}`}
                onMouseEnter={() => prefetchProduct(storeSlug, product.slug)}
                className="block aspect-square overflow-hidden bg-zen-elevated relative"
            >
                {img ? (
                    <img
                        src={img}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-zen-cream-muted text-xs uppercase tracking-widest">
                        No image
                    </div>
                )}
                {product.staffPick && (
                    <div className="absolute top-3 left-3 bg-zen-olive text-zen-bg text-[9px] uppercase tracking-[0.22em] px-2 py-1 inline-flex items-center gap-1">
                        <Star size={9} fill="#F2EDE2" /> Staff Pick
                    </div>
                )}
                {!inStock && (
                    <div className="absolute top-3 right-3 bg-zen-ink/85 text-zen-bg text-[9px] uppercase tracking-[0.22em] px-2 py-1">
                        Out of stock
                    </div>
                )}
            </Link>

            <div className="p-4 sm:p-5 flex flex-col flex-1">
                {/* Promo + sale badges (per-product, from Dutchie's
                    variant.specialConditionIds/specialRewardIds). Sale badge
                    is intentionally LOUD — bold red ribbon at full card
                    width so shoppers can't miss it at a glance. */}
                {(applicableSpecial || isOnSale) && (
                    <div className="flex flex-col gap-1.5 mb-3">
                        {applicableSpecial && (
                            <span
                                data-testid={`product-promo-${product.slug}`}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-red-600 text-white text-[11px] font-semibold uppercase tracking-wider leading-tight max-w-full"
                                title={applicableSpecial.name}
                            >
                                <Tag size={11} className="shrink-0" />
                                <span className="line-clamp-2">
                                    {applicableSpecial.name}
                                </span>
                            </span>
                        )}
                        {isOnSale && !applicableSpecial && (
                            <span
                                data-testid={`product-sticker-${product.slug}`}
                                data-sticker-kind={sticker?.kind || "SALE"}
                                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white text-[13px] sm:text-sm font-bold uppercase tracking-[0.06em] leading-tight"
                            >
                                <Tag size={14} strokeWidth={2.5} className="shrink-0" />
                                <span>
                                    On sale
                                    {sticker?.label ? (
                                        <span className="ml-1.5 font-black tracking-wide">
                                            · {sticker.label}
                                        </span>
                                    ) : null}
                                </span>
                            </span>
                        )}
                    </div>
                )}

                {/* Potency + Strain row */}
                <div className="flex items-center gap-2 flex-wrap mb-2 text-[10px]">
                    {product.potencyThc?.formatted && (
                        <span className="text-emerald-700 font-medium font-mono">
                            THC {product.potencyThc.formatted}
                        </span>
                    )}
                    {product.potencyCbd?.formatted && product.potencyCbd?.range?.[0] > 0 && (
                        <>
                            <span className="text-zen-ink/20">·</span>
                            <span className="text-sky-700 font-medium font-mono">
                                CBD {product.potencyCbd.formatted}
                            </span>
                        </>
                    )}
                    <span className="ml-auto">
                        <StrainPill strain={product.strainType} />
                    </span>
                </div>

                {/* Brand (clickable — opens dedicated brand landing page) */}
                {product.brand?.name && (
                    <Link
                        to={`/menu/${storeSlug}/brand/${brandSlug(product.brand.name)}`}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={() => prefetchBrand(storeSlug, brandSlug(product.brand.name))}
                        data-testid={`product-brand-${product.slug}`}
                        className="text-[11px] text-zen-ink-soft hover:text-zen-olive mb-1.5 truncate inline-block max-w-full underline-offset-2 hover:underline"
                    >
                        {product.brand.name}
                    </Link>
                )}

                {/* Name with weight — text scales down when the title gets
                    long so it always fits two lines without truncating words
                    too aggressively. Threshold tuned to the most common
                    cannabis SKU naming conventions ("Brand | Strain | Form"). */}
                <Link to={`/menu/${storeSlug}/${product.slug}`}>
                    <h3
                        className={`font-serif text-zen-ink font-light leading-snug mb-2 line-clamp-2 hover:text-zen-olive transition-colors ${
                            (product.name || "").length > 44
                                ? "text-sm sm:text-base"
                                : (product.name || "").length > 32
                                  ? "text-base sm:text-base"
                                  : "text-base sm:text-lg"
                        }`}
                    >
                        {product.name}
                        {firstVariant?.option ? (
                            <span className="text-zen-ink-soft font-light"> · {firstVariant.option}</span>
                        ) : null}
                    </h3>
                </Link>

                {/* Price — big = pre-tax, small = after-tax. Tax is computed
                    on variant.priceRec (pre-discount) per NY rules. */}
                <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                        {isOnSale ? (
                            <>
                                <span className="font-serif text-2xl text-zen-ink font-light">
                                    ${firstVariant.specialPriceRec.toFixed(2)}
                                </span>
                                <span className="text-xs text-zen-muted line-through">
                                    ${firstVariant.priceRec.toFixed(2)}
                                </span>
                            </>
                        ) : (
                            <span className="font-serif text-2xl text-zen-ink font-light">
                                {price ? `$${price.toFixed(2)}` : "—"}
                            </span>
                        )}
                    </div>
                    {price ? (
                        <p
                            data-testid={`product-aftertax-${product.slug}`}
                            className="text-[11px] text-zen-ink-soft mt-1"
                        >
                            {fmtRound(withTax(price, firstVariant))}
                            <span className="text-zen-muted ml-1">after tax</span>
                        </p>
                    ) : null}
                </div>

                {/* Add to cart — full width */}
                <button
                    onClick={handleAdd}
                    disabled={!inStock || loading}
                    data-testid={`product-add-${product.slug}`}
                    className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg py-3 text-[11px] uppercase tracking-[0.22em] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {!inStock ? (
                        "Out of stock"
                    ) : (
                        <>
                            <Plus size={14} strokeWidth={2.5} /> Add to cart
                        </>
                    )}
                </button>
            </div>
        </article>
    );
}
